/**
 * Passport · Section B — Site & Ground
 *
 * Renders five environmental signals: Mica, Radon, Flood, Sea-level rise,
 * Boil-notice history. Pure presentational — payload supplied by parent.
 */
import React from 'react';
import { ShieldCheck, ShieldAlert, Waves, Wind, Droplets, ExternalLink } from 'lucide-react';
import { Badge } from '../ui/badge';

const StatusPill = ({ tone, children, testId }) => {
  const toneMap = {
    green:  'bg-emerald-100 text-emerald-800 border-emerald-200',
    amber:  'bg-amber-100 text-amber-900 border-amber-200',
    red:    'bg-red-100 text-red-800 border-red-200',
    stone:  'bg-stone-100 text-stone-600 border-stone-200',
  };
  return (
    <Badge data-testid={testId} className={`${toneMap[tone] || toneMap.stone} text-[10px] py-0`}>
      {children}
    </Badge>
  );
};

const RiskCard = ({ icon: Icon, title, value, tone = 'stone', sub, source, testId, children }) => (
  <div data-testid={testId} className="rounded-lg border border-stone-200 bg-white p-3 space-y-2">
    <div className="flex items-center gap-2">
      <Icon className="h-4 w-4 text-stone-500" />
      <span className="text-[10px] uppercase tracking-wide text-stone-500 font-semibold">{title}</span>
    </div>
    <div className="flex items-center gap-2 flex-wrap">
      <StatusPill tone={tone}>{value}</StatusPill>
    </div>
    {sub && <div className="text-xs text-stone-600">{sub}</div>}
    {children}
    {source && <div className="text-[10px] text-stone-400">Source: {source}</div>}
  </div>
);

const SiteGroundSection = ({ section }) => {
  const d = section.details || {};
  const { mica, radon, flood, sea_level_rise: slr, boil_notices: boil } = d;
  const isUK = section.country_label === 'UK';
  const $ = section.currency_symbol || '€';

  // Card titles adapt to the market's regulatory vocabulary but the card
  // SLOTS (defect risk / radon / flood / water) are identical on IE & UK.
  const defectTitle = isUK ? 'Structural — BGS subsidence' : 'Mica / Pyrite (DCBGS)';
  const defectSource = isUK ? 'BGS GeoSure shrink-swell' : 'gov.ie DCBGS scheme list';
  const waterTitle = isUK ? 'Water supply' : 'Boil-notice history';

  // Tone mapping
  const micaTone =
    mica?.status === 'AFFECTED' ? 'red' :
    mica?.status === 'CLEAR'    ? 'green' : 'stone';
  const radonTone =
    radon?.band === 'HIGH'     ? 'red' :
    radon?.band === 'MODERATE' ? 'amber' :
    radon?.band === 'LOW'      ? 'green' : 'stone';
  const floodTone =
    flood?.class === 'A' ? 'red' :
    flood?.class === 'B' ? 'amber' :
    flood?.class === 'C' ? 'green' : 'stone';
  const boilTone = (boil?.count_last_5y || 0) > 0 ? 'amber' : 'green';

  return (
    <div className="space-y-4 pt-2">
      {/* Top 4 risk cards — identical structure on IE and UK; only the
          card titles + inline values change per market. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <RiskCard
          icon={mica?.status === 'AFFECTED' ? ShieldAlert : ShieldCheck}
          title={defectTitle}
          value={
            mica?.status === 'AFFECTED' ? '⚠ Elevated risk' :
            mica?.status === 'CLEAR'    ? '✓ Low risk' :
            isUK ? 'Add postcode' : 'Add Eircode'
          }
          tone={micaTone}
          sub={mica?.label}
          source={defectSource}
          testId="passport-mica-card"
        >
          {mica?.scheme_url && (
            <a
              href={mica.scheme_url} target="_blank" rel="noopener noreferrer"
              className="text-[11px] text-purple-700 hover:underline inline-flex items-center gap-1"
              data-testid="passport-mica-link"
            >
              Scheme details <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </RiskCard>

        <RiskCard
          icon={Wind}
          title="Radon"
          value={radon?.status || '—'}
          tone={radonTone}
          sub={
            typeof radon?.percent_above_reference === 'number'
              ? `${radon.percent_above_reference}% of local homes exceed 200 Bq/m³`
              : null
          }
          source={radon?.source}
          testId="passport-radon-card"
        >
          {Array.isArray(radon?.mitigation_cost_eur_range) && (
            <div className="text-[11px] text-stone-600">
              Typical mitigation: {$}{radon.mitigation_cost_eur_range[0]}–{$}{radon.mitigation_cost_eur_range[1]}
            </div>
          )}
        </RiskCard>

        <RiskCard
          icon={Waves}
          title={isUK ? 'Flood class (Env Agency)' : 'Flood class (CFRAM)'}
          value={flood?.label || '—'}
          tone={floodTone}
          source={flood?.source}
          testId="passport-flood-card"
        />

        <RiskCard
          icon={Droplets}
          title={waterTitle}
          value={
            boil?.count_last_5y == null
              ? '—'
              : boil.count_last_5y > 0
                ? `${boil.count_last_5y} in last 5 yr`
                : '✓ None in last 5 yr'
          }
          tone={boilTone}
          source={boil?.source}
          testId="passport-boil-card"
        />
      </div>

      {/* SLR strip only when coastal */}
      {slr?.coastal && (
        <div
          className="rounded-lg border border-blue-200 bg-blue-50/50 p-3 flex items-start gap-3"
          data-testid="passport-slr-card"
        >
          <Waves className="h-5 w-5 text-blue-700 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-semibold text-stone-900">Coastal property</div>
            <div className="text-stone-700">
              EPA projects <strong>+{slr.projection_cm_2050} cm</strong> sea-level rise by 2050
              ({slr.scenario}). Site elevation will determine actual risk.
            </div>
            <div className="text-[10px] text-stone-500 mt-1">Source: {slr.source}</div>
          </div>
        </div>
      )}

      <div className="text-[10px] text-stone-400">
        Risk signals are derived from published government datasets. Always
        verify on the official register before bidding.
      </div>
    </div>
  );
};

export default SiteGroundSection;
